import { Direction } from "../types/grid.js";
import type { DungeonMap, GridCell, CellEdges, Position } from "../types/grid.js";
import type { TiledObjectMetadata } from "../types/tiled.js";
import { mapUrl } from "../adventure/ActiveAdventure.js";

// ── Tiled .tmj raw shape ────────────────────────────────────────────────────

interface TiledProperty {
  name: string;
  type: string;
  value: unknown;
}

interface TiledObject {
  id: number;
  name: string;
  /** Tiled ≤1.8 uses `type`; Tiled 1.9+ uses `class`. Support both. */
  type?: string;
  class?: string;
  x: number; // pixel coordinate
  y: number; // pixel coordinate
  width: number;
  height: number;
  properties?: TiledProperty[];
}

interface TiledTileLayer {
  type: "tilelayer";
  name: string;
  width: number;
  height: number;
  /** Row-major flat array of GIDs; 0 = empty (solid), >0 = floor tile. */
  data: number[];
}

interface TiledObjectLayer {
  type: "objectgroup";
  name: string;
  objects: TiledObject[];
}

type TiledLayer = TiledTileLayer | TiledObjectLayer | { type: string; name: string };

interface TiledMap {
  width: number;
  height: number;
  tilewidth: number;
  tileheight: number;
  layers: TiledLayer[];
  /** Map-level custom properties authored in Tiled (e.g. `display_name`). */
  properties?: TiledProperty[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function getObjectType(obj: TiledObject): string {
  return (obj.class ?? obj.type ?? "").toLowerCase();
}

function getProperties(obj: TiledObject): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const prop of obj.properties ?? []) {
    result[prop.name] = prop.value;
  }
  return result;
}

function parseFacing(value: unknown): Direction {
  switch (String(value).toLowerCase()) {
    case "north":
      return Direction.North;
    case "east":
      return Direction.East;
    case "west":
      return Direction.West;
    case "south":
    default:
      return Direction.South;
  }
}

/** Convert a Tiled pixel coordinate to a grid cell index. */
function pixelToGrid(pixel: number, tileSize: number): number {
  return Math.floor(pixel / tileSize);
}

// ── Object metadata parser ───────────────────────────────────────────────────

function str(v: unknown, fallback = ""): string {
  return v !== undefined && v !== null ? String(v) : fallback;
}
function num(v: unknown, fallback = 0): number {
  const n = Number(v);
  return isFinite(n) ? n : fallback;
}
function bool(v: unknown): boolean {
  return v === true || v === "true";
}

/**
 * W21 — parse the `effect` discriminant for an `interact` object.
 * Shape: `effect_kind` in `{teleport, open_door, fire_trigger}`, plus one
 * of `teleporter_id` / `door_id` / `ink_knot`. If `effect_kind` is absent
 * but `teleporter_id` is present, synthesise the teleport variant so
 * in-progress maps authored against the old schema keep working.
 * Returns `null` when no valid effect can be assembled — the caller
 * drops the whole interact record.
 */
function parseInteractEffect(p: Record<string, unknown>): import("../types/tiled.js").InteractEffect | null {
  const kind = String(p["effect_kind"] ?? "").toLowerCase();
  if (kind === "teleport" || (kind === "" && p["teleporter_id"] !== undefined)) {
    const id = str(p["teleporter_id"]);
    return id === "" ? null : { kind: "teleport", teleporter_id: id };
  }
  if (kind === "open_door") {
    const id = str(p["door_id"]);
    return id === "" ? null : { kind: "open_door", door_id: id };
  }
  if (kind === "fire_trigger") {
    const knot = str(p["ink_knot"]);
    return knot === "" ? null : { kind: "fire_trigger", ink_knot: knot };
  }
  if (kind === "toggle_light") {
    // Authored as a comma-separated string, like `spawner.monster_ids`.
    const raw = str(p["light_ids"]);
    const light_ids = raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return light_ids.length === 0 ? null : { kind: "toggle_light", light_ids };
  }
  if (kind === "open_camp_stash") {
    return { kind: "open_camp_stash" };
  }
  return null;
}

/**
 * Convert a raw Tiled object into a typed metadata record.
 * Returns null for unrecognised types so the cell gets no metadata.
 */
function parseObjectMetadata(
  type: string,
  id: number,
  p: Record<string, unknown>,
): TiledObjectMetadata | null {
  switch (type) {
    case "door":
      return {
        type: "door",
        objectId: id,
        door_id: str(p["door_id"], `door-${id}`),
        ...(p["locked"] !== undefined && { locked: Boolean(p["locked"]) }),
        ...(p["lock_dc"] !== undefined && { lock_dc: num(p["lock_dc"]) }),
        ...(p["key_item_id"] !== undefined && { key_item_id: str(p["key_item_id"]) }),
      };

    case "enemy": {
      const base = { type: "enemy" as const, objectId: id, monster_id: str(p["monster_id"], "goblin") };
      return {
        ...base,
        ...(p["leash_radius"] !== undefined && { leash_radius: num(p["leash_radius"], 5) }),
        ...(p["faction"] !== undefined && { faction: str(p["faction"]) }),
        ...(p["count"] !== undefined && { count: num(p["count"], 1) }),
      };
    }

    case "boss": {
      const base = { type: "boss" as const, objectId: id, monster_id: str(p["monster_id"], "boss") };
      return {
        ...base,
        ...(p["death_knot"] !== undefined && { death_knot: str(p["death_knot"]) }),
        ...(p["leash_radius"] !== undefined && { leash_radius: num(p["leash_radius"], 0) }),
        ...(p["faction"] !== undefined && { faction: str(p["faction"]) }),
      };
    }

    case "npc": {
      const base = { type: "npc" as const, objectId: id, monster_id: str(p["monster_id"], "npc"), ink_knot: str(p["ink_knot"]) };
      return {
        ...base,
        ...(p["portrait"] !== undefined && { portrait: str(p["portrait"]) }),
        ...(p["bark"] !== undefined && { bark: str(p["bark"]) }),
        ...(p["pickpocketDC"] !== undefined && { pickpocketDC: num(p["pickpocketDC"]) }),
        ...(p["pickpocketTable"] !== undefined && { pickpocketTable: str(p["pickpocketTable"]) }),
      };
    }

    case "trigger":
      return { type: "trigger", objectId: id, ink_knot: str(p["ink_knot"]) };

    case "exit": {
      const base = { type: "exit" as const, objectId: id };
      return p["requires_boss_dead"] !== undefined
        ? { ...base, requires_boss_dead: bool(p["requires_boss_dead"]) }
        : base;
    }

    case "item":
      return { type: "item", objectId: id, item_id: str(p["item_id"]) };

    case "stairs_down":
      return { type: "stairs_down", objectId: id, target_map: str(p["target_map"]), target_cell: str(p["target_cell"], "1,1") };

    case "stairs_up":
      return { type: "stairs_up", objectId: id, target_map: str(p["target_map"]), target_cell: str(p["target_cell"], "1,1") };

    case "teleporter":
      return { type: "teleporter", objectId: id, target_map: str(p["target_map"]), target_cell: str(p["target_cell"], "1,1"), teleporter_id: str(p["teleporter_id"], String(id)) };

    case "trap_teleporter":
      return { type: "trap_teleporter", objectId: id, target_map: str(p["target_map"]), target_cell: str(p["target_cell"], "1,1") };

    case "interact": {
      const effect = parseInteractEffect(p);
      if (effect === null) return null;
      const modeRaw = String(p["trigger_mode"] ?? "").toLowerCase();
      const trigger_mode: "click" | "step" = modeRaw === "step" ? "step" : "click";
      const interact_id = str(p["interact_id"], `interact-${id}`);
      const subtypeRaw = String(p["subtype"] ?? "").toLowerCase();
      const subtype =
        subtypeRaw === "lever" || subtypeRaw === "button" || subtypeRaw === "plate"
          ? (subtypeRaw as "lever" | "button" | "plate")
          : undefined;
      return {
        type: "interact",
        objectId: id,
        effect,
        interact_id,
        trigger_mode,
        ...(subtype !== undefined && { subtype }),
      };
    }

    case "camp":
      return { type: "camp", objectId: id };

    case "trap": {
      const base = {
        type: "trap" as const,
        objectId: id,
        name: str(p["name"], "Trap"),
        damage_dice: str(p["damage_dice"], "1d6"),
        damage_type: str(p["damage_type"], "piercing"),
        disarm_dc: num(p["disarm_dc"], 12),
      };
      return {
        ...base,
        ...(p["description"] !== undefined ? { description: str(p["description"]) } : {}),
        ...(p["condition_id"] !== undefined ? { condition_id: str(p["condition_id"]) } : {}),
        ...(p["condition_chance"] !== undefined ? { condition_chance: num(p["condition_chance"]) } : {}),
        ...(p["perception_dc"] !== undefined ? { perception_dc: num(p["perception_dc"]) } : {}),
      };
    }

    case "spawner": {
      const raw = str(p["monster_ids"], "");
      const monster_ids = raw
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const base = {
        type: "spawner" as const,
        objectId: id,
        monster_ids,
        trigger_id: str(p["trigger_id"], `spawner-${id}`),
      };
      return p["faction"] !== undefined
        ? { ...base, faction: str(p["faction"]) }
        : base;
    }

    case "prop": {
      const base = {
        type: "prop" as const,
        objectId: id,
        prop_id: str(p["prop_id"], `prop-${id}`),
        kind: str(p["kind"], "crate"),
      };
      const sizeRaw = str(p["size"], "small").toLowerCase();
      const size = sizeRaw === "large" ? ("large" as const) : ("small" as const);
      return {
        ...base,
        ...(p["blocks_movement"] !== undefined ? { blocks_movement: bool(p["blocks_movement"]) } : {}),
        size,
      };
    }

    case "container": {
      const base = {
        type: "container" as const,
        objectId: id,
        container_id: str(p["container_id"], `container-${id}`),
      };
      return {
        ...base,
        ...(p["loot_table"] !== undefined ? { loot_table: str(p["loot_table"]) } : {}),
        ...(p["locked"] !== undefined ? { locked: bool(p["locked"]) } : {}),
        ...(p["lock_dc"] !== undefined ? { lock_dc: num(p["lock_dc"]) } : {}),
        ...(p["key_item_id"] !== undefined ? { key_item_id: str(p["key_item_id"]) } : {}),
      };
    }

    case "light": {
      const initialRaw = String(p["initial_state"] ?? "on").toLowerCase();
      const initial_state: "on" | "off" = initialRaw === "off" ? "off" : "on";
      return {
        type: "light",
        objectId: id,
        light_id: str(p["light_id"], `light-${id}`),
        initial_state,
        color: str(p["color"], "#ffbb88"),
        intensity: num(p["intensity"], 2.2),
        range: num(p["range"], 6),
        height: num(p["height"], 0.9),
      };
    }

    case "secret_door": {
      const base = { type: "secret_door" as const, objectId: id, dc: num(p["dc"], 15) };
      return p["door_id"] !== undefined
        ? { ...base, door_id: str(p["door_id"]) }
        : base;
    }

    default:
      return null;
  }
}

// ── Map parse cache ──────────────────────────────────────────────────────────

const mapCache = new Map<string, DungeonMap>();

/**
 * Returns the cached `DungeonMap` for `mapId`, or fetches, parses, and caches it.
 * The URL is resolved through `ActiveAdventure.mapUrl`, which defaults to the
 * v1 bundle (`/bkcrawl-mvp/maps/<mapId>.tmj`) until boot mounts a
 * different manifest (Z04).
 * Failed fetches are not cached — the next call will retry.
 */
export async function getOrLoadMap(mapId: string): Promise<DungeonMap> {
  const hit = mapCache.get(mapId);
  if (hit !== undefined) return hit;
  const map = await loadTiledMap(mapUrl(mapId));
  mapCache.set(mapId, map);
  return map;
}

/**
 * Evicts one or all entries from the map parse cache.
 * Pass `mapId` to remove a single floor; call with no argument to flush all.
 */
export function clearMapCache(mapId?: string): void {
  if (mapId !== undefined) {
    mapCache.delete(mapId);
  } else {
    mapCache.clear();
  }
}

// ── Teleporter resolver ───────────────────────────────────────────────────────

/**
 * Parses a Tiled `target_cell` string ("x,z") into a grid `Position`.
 * Falls back to `{ x: 1, z: 1 }` for any malformed input.
 */
export function parseTargetCell(raw: string): Position {
  const parts = raw.split(",");
  const x = parseInt(parts[0] ?? "", 10);
  const z = parseInt(parts[1] ?? "", 10);
  return { x: isNaN(x) ? 1 : x, z: isNaN(z) ? 1 : z };
}

/**
 * Resolves an `interact` trigger to its linked teleporter's destination.
 *
 * Searches `map.cells` for a `teleporter` whose `teleporter_id` matches the
 * supplied string, then returns the parsed `{ targetMapId, targetCell }`.
 * Returns null when no matching teleporter exists or `teleporterId` is empty.
 */
export function resolveInteract(
  map: DungeonMap,
  teleporterId: string,
): { targetMapId: string; targetCell: Position } | null {
  if (!teleporterId) return null;
  for (const cell of map.cells) {
    const m = cell.metadata;
    if (m?.type === "teleporter" && m.teleporter_id === teleporterId) {
      return { targetMapId: m.target_map, targetCell: parseTargetCell(m.target_cell) };
    }
  }
  return null;
}

// ── Main export ──────────────────────────────────────────────────────────────

/**
 * Fetches a Tiled .tmj export and converts it into a DungeonMap.
 *
 * Tile convention:
 *   - GID 0  → solid cell (wall block)
 *   - GID >0 → passable floor cell
 *
 * Edge walls are derived from adjacency: every floor cell gets a wall flag on
 * each face where the neighbouring cell is solid or out-of-bounds.
 *
 * Object layer properties:
 *   - type "player_start": sets playerStart + playerStartFacing (via "facing" property)
 *   - all other objects: stored in the cell's `metadata` for use by later systems
 */
export async function loadTiledMap(url: string): Promise<DungeonMap> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load map: ${url} (${response.status})`);
  }
  const raw = (await response.json()) as TiledMap;
  return parseTiledMap(raw, url);
}

/**
 * Pure parser — separated from the fetch so it can be unit-tested without a
 * network or jsdom environment.
 */
export function parseTiledMap(raw: TiledMap, id: string): DungeonMap {
  const { width, height, tilewidth, tileheight, layers } = raw;

  if (!width || !height) {
    throw new Error(`Invalid Tiled map "${id}": missing or zero width/height`);
  }

  // ── 1. Find the primary tile layer ────────────────────────────────────────
  // The first tilelayer is treated as the "floor" layer — GID 0 = solid,
  // GID > 0 = passable. An optional second tilelayer named `no_ceiling`
  // marks outdoor cells (painted tiles = no ceiling, routed to outdoor
  // materials). Any additional tilelayers are ignored.
  const tileLayers = layers.filter((l): l is TiledTileLayer => l.type === "tilelayer");
  const tileLayer = tileLayers[0];
  if (!tileLayer) {
    throw new Error(`Tiled map "${id}" has no tilelayer`);
  }
  const noCeilingLayer = tileLayers.find((l) => l.name === "no_ceiling");
  const data = tileLayer.data;

  // ── 2. Build solid-flag lookup from tile data ─────────────────────────────
  // solid[z][x] = true when the cell has GID 0 (no floor tile)
  const isSolid = (x: number, z: number): boolean => {
    if (x < 0 || x >= width || z < 0 || z >= height) return true;
    return data[z * width + x] === 0;
  };

  const isNoCeiling = (x: number, z: number): boolean => {
    if (!noCeilingLayer) return false;
    if (x < 0 || x >= width || z < 0 || z >= height) return false;
    return (noCeilingLayer.data[z * width + x] ?? 0) > 0;
  };

  // ── 3. Build cell array (row-major, z * width + x) ────────────────────────
  const cells: GridCell[] = Array.from({ length: width * height }, (_, idx) => {
    const x = idx % width;
    const z = Math.floor(idx / width);
    const solid = isSolid(x, z);

    const edges: CellEdges = solid
      ? { north: false, east: false, south: false, west: false }
      : {
          north: isSolid(x, z - 1),
          south: isSolid(x, z + 1),
          east: isSolid(x + 1, z),
          west: isSolid(x - 1, z),
        };

    const noCeiling = !solid && isNoCeiling(x, z);
    return {
      position: { x, z },
      edges,
      solid,
      ...(noCeiling && { noCeiling: true }),
    };
  });

  // ── 4. Parse object layer ─────────────────────────────────────────────────
  let playerStart: Position = { x: 1, z: 1 };
  let playerStartFacing: Direction = Direction.South;

  const objectLayers = layers.filter(
    (l): l is TiledObjectLayer => l.type === "objectgroup",
  );

  // ── 4a. Process `zones` object layer first (T03) ─────────────────────────
  // Rectangle objects in a layer named "zones" carry `texture_set: string`.
  // Each non-solid cell whose center falls inside a zone rect inherits that
  // set. Overlapping zones: last-declared wins (matches Tiled render order
  // for overlapping props; keep authoring mental model consistent).
  const zonesLayer = objectLayers.find((l) => l.name.toLowerCase() === "zones");
  if (zonesLayer) {
    for (const zone of zonesLayer.objects) {
      const props = getProperties(zone);
      const setKey = str(props["texture_set"]).trim();
      if (!setKey) continue;
      // Rect bounds in pixel coords.
      const px0 = zone.x;
      const py0 = zone.y;
      const px1 = zone.x + (zone.width || 0);
      const py1 = zone.y + (zone.height || 0);
      // Cells whose center is inside (px0..px1, py0..py1) get claimed.
      const gx0 = Math.max(0, Math.floor(px0 / tilewidth));
      const gz0 = Math.max(0, Math.floor(py0 / tileheight));
      const gx1 = Math.min(width - 1, Math.floor((px1 - 0.001) / tilewidth));
      const gz1 = Math.min(height - 1, Math.floor((py1 - 0.001) / tileheight));
      for (let gz = gz0; gz <= gz1; gz++) {
        for (let gx = gx0; gx <= gx1; gx++) {
          const cx = gx * tilewidth + tilewidth / 2;
          const cz = gz * tileheight + tileheight / 2;
          if (cx < px0 || cx >= px1 || cz < py0 || cz >= py1) continue;
          const cell = cells[gz * width + gx];
          if (!cell || cell.solid) continue;
          cell.textureSet = setKey;
        }
      }
    }
  }

  // ── 4b. Process remaining object layers (skip zones, handled above) ──────
  for (const objectLayer of objectLayers) {
    if (objectLayer === zonesLayer) continue;
    for (const obj of objectLayer.objects) {
      const objType = getObjectType(obj);
      const props = getProperties(obj);

      const gx = pixelToGrid(obj.x, tilewidth);
      const gz = pixelToGrid(obj.y, tileheight);
      const cellIdx = gz * width + gx;
      const cell = cells[cellIdx];

      if (objType === "player_start") {
        playerStart = { x: gx, z: gz };
        playerStartFacing = parseFacing(props["facing"]);
        continue;
      }

      if (!cell) continue;

      // T04: per-cell texture override via point-object marker. Wins
      // over any zone assignment made in step 4a because this pass
      // runs second and writes to the same cell.textureSet slot.
      if (objType === "texture_cell") {
        const setKey = str(props["texture_set"]).trim();
        if (setKey && !cell.solid) cell.textureSet = setKey;
        continue;
      }

      const meta = parseObjectMetadata(objType, obj.id, props);
      if (meta) cell.metadata = meta;
    }
  }

  // ── 5. Parse map-level display_name (W13) ────────────────────────────────
  const rawDisplayName = raw.properties?.find((p) => p.name === "display_name")?.value;
  const displayName = typeof rawDisplayName === "string" && rawDisplayName ? rawDisplayName : undefined;

  // ── 6. Parse map-level texture_set (T02) ─────────────────────────────────
  const rawTextureSet = raw.properties?.find((p) => p.name === "texture_set")?.value;
  const textureSet =
    typeof rawTextureSet === "string" && rawTextureSet ? rawTextureSet : undefined;

  return {
    id,
    width,
    height,
    cells: Object.freeze(cells),
    playerStart,
    playerStartFacing,
    ...(displayName !== undefined && { displayName }),
    ...(textureSet !== undefined && { textureSet }),
  };
}
